import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import CancelIcon from '@mui/icons-material/Cancel';
import AddCircleIcon from '@mui/icons-material/AddCircle';

// Yazdırılmamış (ilave) ürün tipi
export interface UnprintedItem {
  name: string;
  quantity: number;
  printedQuantity: number;
  unprintedQuantity: number;
}

// İptal edilen ürün tipi
export interface CancelledItem {
  name: string;
  previousQuantity: number; // SQL'deki (yazdırılmış) miktar
  currentQuantity: number; // Sepetteki miktar (0 = tamamen silindi)
  cancelledQuantity: number; // İptal edilen miktar (previousQuantity - currentQuantity)
}

interface UnprintedItemsWarningModalProps {
  open: boolean;
  onClose: () => void;
  onContinue: () => void; // "Yine de devam et" butonuna tıklandığında
  onPrintToKitchen: () => void; // "Mutfağa Yazdır" butonuna tıklandığında (ilave ürünler için)
  onPrintCancellation?: () => void; // ✅ YENİ: "İptal Fişi Yazdır" butonuna tıklandığında
  onPrintBoth?: () => void; // ✅ YENİ: Her ikisini de yazdır (ilave + iptal)
  unprintedItems: UnprintedItem[];
  cancelledItems?: CancelledItem[]; // ✅ YENİ: İptal edilen ürünler
  tableName?: string;
}

const UnprintedItemsWarningModal: React.FC<UnprintedItemsWarningModalProps> = ({
  open,
  onClose,
  onContinue,
  onPrintToKitchen,
  onPrintCancellation,
  onPrintBoth,
  unprintedItems,
  cancelledItems = [],
  tableName,
}) => {
  const totalUnprintedCount = unprintedItems.reduce((sum, item) => sum + item.unprintedQuantity, 0);
  const totalCancelledCount = cancelledItems.reduce((sum, item) => sum + item.cancelledQuantity, 0);
  
  const hasUnprintedItems = unprintedItems.length > 0 && totalUnprintedCount > 0;
  const hasCancelledItems = cancelledItems.length > 0 && totalCancelledCount > 0;
  const hasBoth = hasUnprintedItems && hasCancelledItems;

  // Modal başlığını belirle
  const getTitle = () => {
    if (hasBoth) return 'Yazdırılması Gereken Fişler Var!';
    if (hasCancelledItems) return 'İptal Edilen Ürünler Var!';
    return 'Mutfağa Yazdırılmayan Ürünler Var!';
  };

  // Uyarı rengini belirle
  const getAlertSeverity = () => {
    if (hasCancelledItems) return 'error';
    return 'warning';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      {/* Başlık */}
      <DialogTitle
        sx={{
          bgcolor: hasCancelledItems ? 'error.main' : 'warning.main',
          color: hasCancelledItems ? 'error.contrastText' : 'warning.contrastText',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 2,
        }}
      >
        {hasCancelledItems ? (
          <CancelIcon sx={{ fontSize: 28 }} />
        ) : (
          <WarningAmberIcon sx={{ fontSize: 28 }} />
        )}
        <Typography variant="h6" component="span" fontWeight="bold">
          {getTitle()}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* ✅ İPTAL EDİLEN ÜRÜNLER BÖLÜMÜ */}
        {hasCancelledItems && (
          <>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body1">
                {tableName ? `${tableName} masasında ` : ''}Mutfağa yazdırıldıktan sonra <strong>{totalCancelledCount} adet</strong> ürün iptal edilmiş.
                İptal fişi yazdırılması gerekiyor!
              </Typography>
            </Alert>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="error.main" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CancelIcon fontSize="small" />
                İptal Edilen Ürünler:
              </Typography>
              <List
                dense
                sx={{
                  bgcolor: 'error.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'error.200',
                  maxHeight: 150,
                  overflowY: 'auto',
                }}
              >
                {cancelledItems.map((item, index) => (
                  <React.Fragment key={`cancelled-${index}`}>
                    {index > 0 && <Divider />}
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CancelIcon color="error" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight="medium" color="error.main">
                            {item.name}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            Yazdırılmış: {item.previousQuantity} → Şimdi: {item.currentQuantity} | 
                            <strong style={{ color: '#d32f2f' }}> İPTAL: {item.cancelledQuantity} adet</strong>
                          </Typography>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </Box>
          </>
        )}

        {/* ✅ YAZDIRILAMMIŞ ÜRÜNLER BÖLÜMÜ */}
        {hasUnprintedItems && (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body1">
                {tableName && !hasCancelledItems ? `${tableName} masasında ` : ''}
                {hasCancelledItems ? 'Ayrıca, ' : ''}
                Mutfağa yazdırılmamış <strong>{totalUnprintedCount} adet</strong> yeni ürün bulunmaktadır.
              </Typography>
            </Alert>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="warning.main" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AddCircleIcon fontSize="small" />
                Yazdırılmamış (İlave) Ürünler:
              </Typography>
              <List
                dense
                sx={{
                  bgcolor: 'warning.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'warning.200',
                  maxHeight: 150,
                  overflowY: 'auto',
                }}
              >
                {unprintedItems.map((item, index) => (
                  <React.Fragment key={`unprinted-${index}`}>
                    {index > 0 && <Divider />}
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <RestaurantMenuIcon color="warning" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight="medium">
                            {item.name}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            Toplam: {item.quantity} adet | Yazdırılmamış: <strong style={{ color: '#ed6c02' }}>{item.unprintedQuantity} adet</strong>
                          </Typography>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </Box>
          </>
        )}

        {/* Bilgilendirme */}
        <Typography variant="body2" color="text.secondary">
          {hasBoth 
            ? 'Her iki fişi de yazdırmanız önerilir. Yazdırmadan çıkmak istiyor musunuz?'
            : hasCancelledItems 
              ? 'İptal fişi yazdırılmazsa mutfak iptalden haberdar olmayacaktır. Yine de devam etmek istiyor musunuz?'
              : 'Mutfağa yazdırmadan çıkarsanız, bu ürünler mutfağa iletilmeyecektir. Yine de devam etmek istiyor musunuz?'
          }
        </Typography>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, gap: 1, flexDirection: 'column' }}>
        {/* ✅ HER İKİSİNİ DE YAZDIR (Hem ilave hem iptal varsa) */}
        {hasBoth && onPrintBoth && (
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<PrintIcon />}
            onClick={() => {
              onPrintBoth();
              onClose();
            }}
            fullWidth
            sx={{
              py: 1.5,
              fontWeight: 'bold',
              fontSize: '1rem',
            }}
          >
            Her İkisini de Yazdır (İlave + İptal)
          </Button>
        )}

        {/* ✅ SADECE İLAVE YAZDIR */}
        {hasUnprintedItems && !hasBoth && (
          <Button
            variant="contained"
            color="success"
            size="large"
            startIcon={<PrintIcon />}
            onClick={() => {
              onPrintToKitchen();
              onClose();
            }}
            fullWidth
            sx={{
              py: 1.5,
              fontWeight: 'bold',
              fontSize: '1rem',
            }}
          >
            Mutfağa Yazdır (İlave)
          </Button>
        )}

        {/* ✅ SADECE İPTAL YAZDIR */}
        {hasCancelledItems && !hasBoth && onPrintCancellation && (
          <Button
            variant="contained"
            color="error"
            size="large"
            startIcon={<CancelIcon />}
            onClick={() => {
              onPrintCancellation();
              onClose();
            }}
            fullWidth
            sx={{
              py: 1.5,
              fontWeight: 'bold',
              fontSize: '1rem',
            }}
          >
            İptal Fişi Yazdır
          </Button>
        )}

        {/* ✅ AYRI AYRI YAZDIR BUTONLARI (Her ikisi de varsa) */}
        {hasBoth && (
          <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
            <Button
              variant="outlined"
              color="success"
              size="medium"
              startIcon={<AddCircleIcon />}
              onClick={() => {
                onPrintToKitchen();
              }}
              sx={{ flex: 1, py: 1 }}
            >
              Sadece İlave
            </Button>
            {onPrintCancellation && (
              <Button
                variant="outlined"
                color="error"
                size="medium"
                startIcon={<CancelIcon />}
                onClick={() => {
                  onPrintCancellation();
                }}
                sx={{ flex: 1, py: 1 }}
              >
                Sadece İptal
              </Button>
            )}
          </Box>
        )}

        {/* İkincil Buton: Yine de Devam Et */}
        <Button
          variant="outlined"
          color={hasCancelledItems ? 'error' : 'warning'}
          size="medium"
          startIcon={<ArrowBackIcon />}
          onClick={() => {
            onContinue();
            onClose();
          }}
          fullWidth
          sx={{
            py: 1,
          }}
        >
          Yine de Devam Et (Yazdırmadan Çık)
        </Button>

        {/* İptal Butonu */}
        <Button
          variant="text"
          color="inherit"
          size="small"
          onClick={onClose}
          sx={{
            mt: 0.5,
            color: 'text.secondary',
          }}
        >
          İptal
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnprintedItemsWarningModal;
